import { useMutation } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useGeneratePosterFromVideoAttachment } from '@bonusly/common-action-input';
import { useToast } from '@bonusly/design-system';
import { useHttpClient } from '@bonusly/http-client';

import { Video, VideoStatus } from '../types/announcements';

interface UploadVideoResponse {
	id: string;
	video_thumbnail_url?: string | null;
	video_poster_url?: string | null;
	video_transcript_url?: string | null;
	video_hd_url?: string | null;
	video_sd_url?: string | null;
	video_original_url?: string | null;
}

interface UseUploadAnnouncementVideoOptions {
	onSuccess?: (video: Video) => void;
	onError?: (message: string) => void;
}

export const useUploadAnnouncementVideo = ({ onSuccess, onError }: UseUploadAnnouncementVideoOptions = {}) => {
	const http = useHttpClient('v2');
	const { t } = useTranslation('announcements', { keyPrefix: 'modal' });
	const toast = useToast();
	const abortControllerRef = useRef<AbortController | null>(null);
	const generatePoster = useGeneratePosterFromVideoAttachment();

	const mutation = useMutation<Video, Error, { file: File }>(
		async ({ file }) => {
			// Capture the controller locally so a second upload starting before this
			// one resolves cannot swap the signal out from under us mid-flight.
			const controller = new AbortController();
			abortControllerRef.current = controller;
			const form = new FormData();
			form.append('video[name]', file.name);
			form.append('video[file]', file);

			// Generate a poster client-side so Safari/iOS can render a still image
			// while the server completes async transcoding/transcription.
			const { posterFile } = await generatePoster(file);
			// If the user cancelled while the poster was generating, surface a
			// CanceledError now rather than firing a network request we'll have
			// to cancel anyway. onError filters this out (no toast).
			if (controller.signal.aborted) {
				const err = new DOMException('Upload cancelled', 'AbortError');
				throw err;
			}
			if (posterFile) {
				form.append('video[poster]', posterFile);
			}

			const response = await http.post<UploadVideoResponse>('/videos', form, {
				signal: controller.signal,
			});

			const data = response.data;
			return {
				video_id: data.id,
				status: VideoStatus.Processing,
				video_thumbnail_url: data.video_thumbnail_url ?? null,
				video_poster_url: data.video_poster_url ?? null,
				video_transcript_url: data.video_transcript_url ?? null,
				video_hd_url: data.video_hd_url ?? null,
				video_sd_url: data.video_sd_url ?? null,
				video_original_url: data.video_original_url ?? null,
			};
		},
		{
			onSuccess,
			onError: (error: any) => {
				if (error?.name === 'CanceledError' || error?.name === 'AbortError') {
					return;
				}
				const message = error?.response?.data?.error || error?.message || t('video_upload_error_generic');
				toast({
					title: t('video_upload_error', { error: message }),
					status: 'error',
					position: 'bottom-right',
				});
				onError?.(message);
			},
		}
	);

	const uploadVideo = (file: File) => mutation.mutateAsync({ file });

	const cancelUpload = () => {
		abortControllerRef.current?.abort();
		abortControllerRef.current = null;
	};

	// Marks an uploaded-but-unattached video for deletion on the server. Best-
	// effort: errors are swallowed because (a) a daily sweeper also cleans up
	// orphans, and (b) we don't want to block the user's UI on cleanup.
	const discardVideo = useCallback(
		async (videoId: string) => {
			try {
				await http.patch(`/videos/${videoId}/discard`);
			} catch (error) {
				// Best-effort cleanup; the daily sweeper will still pick it up.
			}
		},
		[http]
	);

	return {
		uploadVideo,
		cancelUpload,
		discardVideo,
		isUploading: mutation.isLoading,
	};
};
